/**
 * Weapon clash (lane C0-WEAPON): when two weapons meet — a blow on a raised guard, a parry, a counter, the duel's guard
 * break, a guarded blow inside a lock form — a bright clash flash, steel sparks fanned out from the meeting point, a
 * thin ring, embers, a short hit-stop and a screen flash, all scaled by the blow's weight (data/hitWeight.ts). A palm
 * or fist meeting a blade (or bare hands meeting) is a dull thud instead: a soft puff, a ring of dust and a few chips,
 * no streaks. Sounds: sfx/sounds/clash.ts.
 *
 * Read from the shared events only (rules stay in packages/shared):
 * - `hit` with outcome `blocked` (a guarded blow; lock forms' guarded blows too), `guardBreak` (破 / the duel's guard
 *   break) or `interrupt` (a wind-up cut short: blade met blade);
 * - `pvpDuel` phases `parry` (a timed parry) and `counter` (a counter stance turning the blow).
 *
 * The drawing is a pure function of (time, spec), deterministic (hash noise, no Math.random), so the review page draws
 * exactly what the game does. It is not wired into the game by itself: `Effects.handle` calls `clashFromEvent` (one
 * line, main's edit; see the lane report). Ideas ported from the C0-VFX clone's impact layer (apps/c0-vfx
 * render/ink/spectacle.ts: the starburst flash and falling sparks), rebuilt for the clash.
 */
import { HIT_WEIGHT, hitWeightOf, type Entity, type WorldEvent } from '@nine/shared';
import type { FxHost } from './registry';

export type ClashKind = 'steel' | 'dull';
/** 0: a light blow on the guard, 1: a strong one or a parry, 2: a guard break or a won interrupt. */
export type ClashWeight = 0 | 1 | 2;

export interface ClashSpec {
  kind: ClashKind;
  weight: ClashWeight;
  /** Screen direction of the blow (+1: from left to right). */
  dir: number;
  seed: number;
}

/** Seconds a clash plays. */
export const CLASH_LIFE = 0.5;

/** Hit-stop (s) by weight: a guarded blow a beat, the data's medium and heavy holds above it. */
export const CLASH_HITSTOP: Readonly<Record<ClashWeight, number>> = {
  0: 0.035,
  1: Math.max(0.05, HIT_WEIGHT.classes.medium.hitStop),
  2: HIT_WEIGHT.classes.heavy.hitStop,
};

/** The master's palette: warm white, gold, orange, red; the thud in warm greys and browns. */
const STEEL = { core: '#ffffff', hot: '#fff3c4', gold: '#ffd25a', orange: '#ff9a3a', red: '#d8402e' };
const DULL = {
  core: '#ffffff',
  puff: '#f6ead4',
  dust: '#d9c7a8',
  ring: '#7a6450',
  chip: '#8a735a',
  dark: '#4e3c2c',
};

function hash2(x: number, y: number, seed = 0): number {
  let h = Math.imul(x | 0, 374761393) ^ Math.imul(y | 0, 668265263) ^ Math.imul(seed | 0, 2147483647);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
const easeOut = (t: number): number => 1 - (1 - clamp01(t)) ** 3;

function dot(g: CanvasRenderingContext2D, x: number, y: number, c: string, s = 1): void {
  g.fillStyle = c;
  g.fillRect(Math.round(x), Math.round(y), s, s);
}

/** The clash flash: a four-point starburst with diagonal rays (steel), white core over gold, red tips when heavy. */
function starburst(g: CanvasRenderingContext2D, X: number, Y: number, tau: number, w: ClashWeight): void {
  const dur = 0.07 + 0.03 * w;
  if (tau > dur) return;
  const k = tau / dur;
  const r = Math.round((7 + 5 * w) * (0.6 + 0.6 * easeOut(k)) * (1 - 0.35 * k));
  g.fillStyle = w === 2 ? STEEL.red : STEEL.orange;
  g.fillRect(X - r - 1, Y, r * 2 + 3, 1);
  g.fillStyle = STEEL.gold;
  g.fillRect(X - r, Y - 1, r * 2 + 1, 3);
  g.fillRect(X - 1, Y - Math.round(r * 0.8), 3, Math.round(r * 1.6) + 1);
  const d = Math.round(r * 0.55);
  for (let i = 1; i <= d; i++) {
    const c = i > d - 1 ? STEEL.orange : STEEL.gold;
    dot(g, X - i, Y - i, c);
    dot(g, X + i, Y - i, c);
    dot(g, X - i, Y + i, c);
    dot(g, X + i, Y + i, c);
  }
  g.fillStyle = STEEL.hot;
  g.fillRect(X - r + 2, Y, r * 2 - 3, 1);
  g.fillRect(X, Y - Math.round(r * 0.8) + 2, 1, Math.round(r * 1.6) - 3);
  const c = k < 0.5 ? 1 + w : w;
  g.fillStyle = STEEL.core;
  g.fillRect(X - c, Y - c, c * 2 + 1, c * 2 + 1);
}

/** A thin pixel ring growing out (squashed to the ground's tilt), stepped thinner as it fades. */
function ring(
  g: CanvasRenderingContext2D,
  X: number,
  Y: number,
  tau: number,
  r0: number,
  r1: number,
  dur: number,
  c: string,
  seed: number,
): void {
  if (tau > dur) return;
  const k = tau / dur;
  const r = r0 + (r1 - r0) * easeOut(k);
  const n = Math.max(12, Math.round(r * 3));
  const keep = 1 - k;
  for (let i = 0; i < n; i++) {
    if (hash2(i, 5, seed) > keep + 0.15) continue;
    const a = (i / n) * Math.PI * 2;
    dot(g, X + Math.cos(a) * r, Y + Math.sin(a) * r * 0.55, c);
  }
}

/** Steel sparks: streaks fanned out of the meeting point, mostly up and to both sides of the blow, falling as they fly. */
function sparks(g: CanvasRenderingContext2D, X: number, Y: number, tau: number, s: ClashSpec): void {
  const n = [12, 18, 28][s.weight]!;
  const dur = 0.32 + 0.08 * s.weight;
  if (tau > dur) return;
  for (let i = 0; i < n; i++) {
    const h1 = hash2(i, 1, s.seed);
    const h2 = hash2(i, 2, s.seed);
    const h3 = hash2(i, 3, s.seed);
    // Each spark's own short life: some die early, so the burst thins out instead of vanishing at once.
    const life = dur * (0.45 + 0.55 * h3);
    if (tau > life) continue;
    // A fan opening upward (−90° ± 80°), leaning a little the way the blow went; a few thrown back along it.
    const back = h1 < 0.18;
    const a = back
      ? (s.dir > 0 ? Math.PI : 0) + (h2 - 0.5) * 0.9
      : -Math.PI / 2 + (h1 - 0.5) * 2.8 + s.dir * 0.25;
    const v = (80 + 130 * h2) * (1 + 0.35 * s.weight);
    const x = X + Math.cos(a) * v * tau;
    const y = Y + Math.sin(a) * v * tau + 160 * tau * tau;
    const u = tau / life;
    const len = Math.max(u < 0.6 ? 2 : 1, Math.round((4 + s.weight) * (1 - u)));
    const vx = Math.cos(a);
    const vy = Math.sin(a) + (320 * tau) / Math.max(1, v);
    const vl = Math.hypot(vx, vy) || 1;
    for (let k = 0; k < len; k++)
      dot(
        g,
        x - (vx / vl) * k,
        y - (vy / vl) * k,
        k === 0
          ? u < 0.4
            ? STEEL.core
            : STEEL.hot
          : u < 0.5
            ? STEEL.gold
            : k === len - 1
              ? STEEL.red
              : STEEL.orange,
      );
  }
}

/** Embers: a few slow dots that drift down after the burst (strong and heavy clashes). */
function embers(g: CanvasRenderingContext2D, X: number, Y: number, tau: number, s: ClashSpec): void {
  if (s.weight === 0 || tau < 0.08) return;
  const n = 4 * s.weight;
  for (let i = 0; i < n; i++) {
    const u = (tau - 0.08) / (0.3 + 0.12 * hash2(i, 7, s.seed));
    if (u >= 1) continue;
    const a = (hash2(i, 8, s.seed) - 0.5) * 2.6 - Math.PI / 2;
    const r = 6 + 14 * hash2(i, 9, s.seed);
    dot(
      g,
      X + Math.cos(a) * r * easeOut(u) + s.dir * u * 3,
      Y + Math.sin(a) * r * easeOut(u) + 10 * u * u,
      (i + Math.floor(u * 6)) % 2 ? STEEL.orange : STEEL.gold,
    );
  }
}

/** The thud of a palm or fist on a blade (or on a guard): a soft round puff, a flat ring of dust, a few chips. */
function thud(g: CanvasRenderingContext2D, X: number, Y: number, tau: number, s: ClashSpec): void {
  const w = s.weight;
  const pd = 0.1 + 0.04 * w;
  if (tau < pd) {
    const k = tau / pd;
    const r = Math.round((4 + 2 * w) * (0.7 + 0.6 * easeOut(k)));
    for (let y = -r; y <= r; y++)
      for (let x = -r; x <= r; x++) {
        const d = (x * x + y * y) / (r * r);
        if (d > 1) continue;
        // Lit from the upper right, as everything is.
        const lit = x * 0.6 - y * 0.8 > r * 0.2;
        dot(
          g,
          X + x,
          Y + y,
          d > 0.78 ? DULL.ring : d < 0.3 && k < 0.5 ? DULL.core : lit ? DULL.puff : DULL.dust,
        );
      }
  }
  ring(g, X, Y + 2, tau, 4 + w, 14 + 6 * w, 0.24 + 0.06 * w, DULL.ring, s.seed + 3);
  // Short impact dashes round the puff (the blow's push, read at a glance; no glint).
  if (tau < 0.12)
    for (let i = 0; i < 6 + 2 * w; i++) {
      const a = (i / (6 + 2 * w)) * Math.PI * 2 + 0.3;
      const r0 = 6 + 2 * w + 40 * tau;
      for (let q = 0; q < 2 + w; q++)
        dot(g, X + Math.cos(a) * (r0 + q), Y + Math.sin(a) * (r0 + q) * 0.7, DULL.ring);
    }
  const n = [5, 8, 12][w]!;
  for (let i = 0; i < n; i++) {
    const life = 0.22 + 0.12 * hash2(i, 3, s.seed);
    if (tau > life) continue;
    const a = -Math.PI / 2 + (hash2(i, 1, s.seed) - 0.5) * 2.4 + s.dir * 0.4;
    const v = 40 + 50 * hash2(i, 2, s.seed);
    dot(
      g,
      X + Math.cos(a) * v * tau,
      Y + Math.sin(a) * v * tau + 200 * tau * tau,
      i % 3 ? DULL.chip : DULL.dark,
      i % 4 === 0 ? 2 : 1,
    );
  }
}

/** A clash `tau` seconds in, at screen point (`x`, `y`) (world px, offset added). */
export function drawClash(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  tau: number,
  s: ClashSpec,
): void {
  if (tau < 0 || tau > CLASH_LIFE) return;
  const X = Math.round(x);
  const Y = Math.round(y);
  if (s.kind === 'dull') {
    thud(g, X, Y, tau, s);
    return;
  }
  ring(g, X, Y, tau, 3, 12 + 7 * s.weight, 0.18 + 0.05 * s.weight, STEEL.gold, s.seed + 1);
  if (s.weight === 2) ring(g, X, Y, tau - 0.04, 4, 26, 0.26, STEEL.orange, s.seed + 2);
  sparks(g, X, Y, tau, s);
  embers(g, X, Y, tau, s);
  starburst(g, X, Y, tau, s.weight);
}

/** The screen flash, zoom punch and shake of a clash (`FxHost.impact`). */
export function clashImpact(s: ClashSpec): { col: string; flash: number; punch: number; shake: number } {
  const w = s.weight;
  return s.kind === 'steel'
    ? { col: '255,236,190', flash: [0.05, 0.11, 0.2][w]!, punch: [0, 0.12, 0.32][w]!, shake: [1.5, 3, 5][w]! }
    : {
        col: '240,226,200',
        flash: [0.02, 0.05, 0.1][w]!,
        punch: [0, 0.08, 0.22][w]!,
        shake: [1.2, 2.4, 4][w]!,
      };
}

/** The clash an event makes, if any: its kind (from whether both hold a weapon) and weight. */
export function clashSpecOf(
  ev: WorldEvent,
  armed: (id: number) => boolean,
): { spec: Omit<ClashSpec, 'dir' | 'seed'>; a: number; b: number } | null {
  if (ev.type === 'hit') {
    if (ev.outcome !== 'blocked' && ev.outcome !== 'guardBreak' && ev.outcome !== 'interrupt') return null;
    const steel = armed(ev.srcId) && armed(ev.tgtId);
    // A blow on the guard as heavy as the move behind it would have landed; a break or a won interrupt at the top.
    const landed = hitWeightOf(ev.skillId, 'hit');
    const weight: ClashWeight =
      ev.outcome !== 'blocked' ? 2 : landed && (landed.cls === 'medium' || landed.cls === 'heavy') ? 1 : 0;
    return { spec: { kind: steel ? 'steel' : 'dull', weight }, a: ev.srcId, b: ev.tgtId };
  }
  if (ev.type === 'pvpDuel' && (ev.phase === 'parry' || ev.phase === 'counter') && ev.parry !== undefined) {
    const other = ev.parry === ev.a ? ev.b : ev.a;
    const steel = armed(ev.parry) && armed(other);
    return { spec: { kind: steel ? 'steel' : 'dull', weight: 1 }, a: other, b: ev.parry };
  }
  return null;
}

let seedN = 1;

/**
 * Plays the clash for an event through the game's effects: the flash and sparks between the two fighters, nearer the
 * one struck, at chest height; the screen flash; the attacker's swing held for the hit-stop. Returns whether it drew.
 */
export function clashFromEvent(host: FxHost, ev: WorldEvent, armed: (e: Entity) => boolean): boolean {
  const w = host.world;
  const c = clashSpecOf(ev, (id) => {
    const e = w.get(id);
    return !!e && armed(e);
  });
  if (!c) return false;
  const src = w.get(c.a);
  const tgt = w.get(c.b);
  if (!tgt) return false;
  const [tx, ty] = host.pos(tgt);
  const [sx, sy] = src ? host.pos(src) : [tx - 10, ty];
  const dir = Math.sign(tx - sx) || 1;
  // Where the blades meet: a third of the way from the one struck toward the striker, at the guard's height.
  const x = tx + (sx - tx) * 0.33;
  const y = ty + (sy - ty) * 0.33 - 16 * tgt.scale * host.tall(tgt) - tgt.z;
  const spec: ClashSpec = { ...c.spec, dir, seed: (seedN = (seedN * 1103515245 + 12345) & 0x7fffffff) };
  host.fx.push({
    type: 'custom',
    x,
    y,
    t: 0,
    life: CLASH_LIFE,
    draw: (g, X, Y, k) => drawClash(g, X, Y, k * CLASH_LIFE, spec),
  });
  const im = clashImpact(spec);
  host.impact(im.col, im.flash, im.punch, im.shake);
  // The hit-stop: the striker's swing frozen on its contact frame (renderer.ts holds it; see the lane report).
  if (src) {
    const a = host.anim(src.id);
    a.hitstopAt = w.time;
    a.hitstopHold = CLASH_HITSTOP[spec.weight];
  }
  return true;
}
